'use client';

import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, HStack, Stack, Text, Textarea } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import orderService from '@/_service/orders.service';
import { getTrackingStatus } from '@/_utils/getStatus';
import { Rating, RoundedStar } from '@smastrom/react-rating';
import { useTranslations } from 'next-intl';
import reviewService from '@/_service/review.service';
import { toaster } from '@/components/ui/toaster';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { IRedux } from '@/_store/types';
import { useSelector } from 'react-redux';

const ReviewOrder = () => {
  const [open, setOpen] = useState(false)
  const [rate, setRate] = useState(5)
  const [reviewMsg, setReviewMsg] = useState('')

  const t = useTranslations()
  const currentLocale = useCustomLocale()
  const { user } = useSelector((state: IRedux) => state.auth)

  const { data: ordersData } = useQuery<any>({
    queryKey: ['get-all-orders'],
    queryFn: () =>
      orderService
        .getList(
          user?.id || '',
          {
            page: 1,
            limit: 20,
          },
          {
            Authorization: user?.access_token,
          }
        )
        .then((res) => res?.data),
    enabled: Boolean(user?.id) && open,
  })
  const latestOrder = ordersData?.orders?.[0]

  const status: any = latestOrder?.status_id
    ? getTrackingStatus(latestOrder.status_id)
    : undefined

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const review = window.sessionStorage.getItem('review') || ''
      if (!review) {
        setOpen(true)
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const review = window.sessionStorage.getItem('review') || ''
      if (!review) {
        window.sessionStorage.setItem('review', 'shown')
      }
    }
  }, [])

  const onSubmitReview = () => {
    const data = {
      lang: currentLocale,
      branch_id: latestOrder?.steps?.[0]?.branch_id,
      branch_name: latestOrder?.steps?.[0]?.branch_name,
      client_id: latestOrder?.client_id,
      client_name: latestOrder?.client_name,
      client_phone: latestOrder?.client_phone_number,
      courier_id: latestOrder?.courier_id,
      courier_name: `${latestOrder?.courier?.first_name} ${latestOrder?.courier?.last_name}`,
      delivery_time: latestOrder?.delivery_time,
      operator_id: latestOrder?.shipper_user?.id,
      operator_name: latestOrder?.shipper_user?.name,
      order_id: latestOrder?.id,
      order_num: latestOrder?.external_order_id,
      rating: rate,
      related_subject: 'meal',
      review_message: reviewMsg,
      type: rate > 3 ? 'like' : 'dislike',
    }

    reviewService
      .create(data)
      .then(() => {
        toaster.create({
          title: t('successfully_sent'),
          type: 'success',
        })
        setOpen(false)
      })
      .catch(console.log)
  }

  return (
    <>
      <DialogRoot
        lazyMount
        open={
          open &&
          !Boolean(latestOrder?.review_id) &&
          (status === 'delivered' || status === 'finished')
        }
        onOpenChange={() => setOpen(false)}
        placement={'center'}
      >
        <DialogContent mx={4} rounded={12}>
          <DialogHeader p={4}>
            <DialogTitle py={0}>
              <Text fontSize='lg' maxW={'90%'} fontWeight={700}>
                {t('your_order_has_been_successfully')}
              </Text>
              <Text fontSize={'sm'}>
                {t('please_rate_your_order')} {latestOrder?.external_order_id}
              </Text>
            </DialogTitle>
          </DialogHeader>
          <DialogBody px={4} py={0}>
            <HStack mb={3} align='center' justify='space-between'>
              {!latestOrder?.review && (
                <Rating
                  readOnly={Boolean(latestOrder?.review_id)}
                  style={{ maxWidth: 150 }}
                  value={!latestOrder?.review_id ? rate : latestOrder?.rating}
                  itemStyles={{
                    itemShapes: RoundedStar,
                    activeFillColor: '#FFC500',
                    inactiveFillColor: '#E0E8F1',
                  }}
                  onChange={(newValue: number) => {
                    setRate(newValue)
                  }}
                />
              )}
            </HStack>
            <Stack>
              <Textarea
                size={'xl'}
                rounded={16}
                height={'100px'}
                variant='subtle'
                placeholder={t('describe_your_impressions')}
                value={reviewMsg}
                onChange={(e) => setReviewMsg(e.target.value)}
              />
            </Stack>
          </DialogBody>
          <DialogFooter p={4}>
            <Button
              onClick={onSubmitReview}
              bg='primary'
              color='primary.text'
              _hover={{ bg: 'primary.hover' }}
              rounded={12}
              w={'full'}
              size={'2xl'}
              height={'56px'}
              disabled={rate < 1}
            //   loading={sent}
            >
              {t('rate_your_order')}
            </Button>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  )
}
export default ReviewOrder;
